// A simple tick-rate-aware loop runner.
//
// Wraps a `step()`-capable thing (typically the adapter from `createRustSimAdapter`)
// and drives it at a fixed simulation rate, decoupled from the host's frame rate.
//
// Runtime controls:
//   - `start()` / `stop()` — full lifecycle
//   - `pause()` / `resume()` — keep the loop alive but stop stepping; ticks
//      accumulator is reset on resume so we don't burst-catchup the gap
//   - `setSpeed(n)` — multiply effective tick rate (1× / 2× / 4× …); does not
//      change `tickRateHz` itself, so the simulation's nominal rate stays
//      stable for replay / determinism
//
// Usage:
//   const loop = new TickLoop({ tickRateHz: 10, step: (n) => adapter.step(n) });
//   loop.start();
//   loop.pause();      // loop alive, no ticks emitted
//   loop.setSpeed(2);  // 2× the nominal rate
//   loop.resume();
//   loop.stop();

export type TickLoopRunState = 'stopped' | 'running' | 'paused';

export interface TickLoopOptions {
  /** Simulation tick rate in Hz. Default 10 (matches docs/06-tech/netcode.md). */
  readonly tickRateHz?: number;
  /** Steps the simulation by `n` ticks. */
  readonly step: (n: number) => void;
  /** Called after each batch of ticks; useful for snapshot push to WS. */
  readonly onTick?: (tickCount: number) => void;
  /**
   * Maximum batched catch-up after a long pause (default 30 ticks). Prevents
   * post-tab-switch death spirals.
   */
  readonly maxCatchUpTicks?: number;
  /** Initial speed multiplier (default 1). Negative or 0 → coerced to 1. */
  readonly initialSpeed?: number;
  /**
   * Schedule fn — defaults to `setTimeout`. Override in tests with a fake
   * scheduler for deterministic stepping.
   */
  readonly schedule?: (cb: () => void, ms: number) => unknown;
  readonly cancel?: (handle: unknown) => void;
}

export class TickLoop {
  private readonly nominalIntervalMs: number;
  private readonly maxCatchUpTicks: number;
  private readonly stepFn: (n: number) => void;
  private readonly onTick?: (tickCount: number) => void;
  private readonly schedule: (cb: () => void, ms: number) => unknown;
  private readonly cancel: (handle: unknown) => void;

  private state: TickLoopRunState = 'stopped';
  private handle: unknown = null;
  private accumulatorMs = 0;
  private lastWallMs = 0;
  private speed = 1;

  constructor(options: TickLoopOptions) {
    const hz = options.tickRateHz ?? 10;
    this.nominalIntervalMs = 1000 / hz;
    this.maxCatchUpTicks = options.maxCatchUpTicks ?? 30;
    this.stepFn = options.step;
    this.onTick = options.onTick;
    this.schedule = options.schedule ?? ((cb, ms) => setTimeout(cb, ms));
    this.cancel = options.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.speed = sanitizeSpeed(options.initialSpeed);
  }

  // -------- lifecycle ----------------------------------------------------

  start(): void {
    if (this.state === 'running') return;
    this.state = 'running';
    this.lastWallMs = nowMs();
    this.accumulatorMs = 0;
    this.scheduleNext();
  }

  stop(): void {
    this.state = 'stopped';
    this.cancelHandle();
  }

  /**
   * Stop emitting ticks but keep the loop scheduled. Resume picks up with
   * a fresh accumulator — i.e., paused time is "lost," not catch-up'd.
   */
  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.cancelHandle();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.lastWallMs = nowMs();
    this.accumulatorMs = 0;
    this.scheduleNext();
  }

  // -------- runtime controls --------------------------------------------

  /** Multiply the effective tick rate. setSpeed(2) → ticks fire ~2× as fast. */
  setSpeed(multiplier: number): void {
    this.speed = sanitizeSpeed(multiplier);
    // Reset the accumulator so the new rate kicks in cleanly without a
    // burst from the old rate's accumulated time.
    this.accumulatorMs = 0;
    this.lastWallMs = nowMs();
  }

  getSpeed(): number {
    return this.speed;
  }

  getRunState(): TickLoopRunState {
    return this.state;
  }

  /** Manually advance by `ticks`. Useful for headless / replay. */
  forceStep(ticks: number): void {
    if (ticks <= 0) return;
    this.stepFn(ticks);
    this.onTick?.(ticks);
  }

  // -------- internals ----------------------------------------------------

  private scheduleNext(): void {
    if (this.state !== 'running') return;
    // Effective interval — higher speed ⇒ shorter wait between frames.
    const intervalMs = this.nominalIntervalMs / this.speed;
    this.handle = this.schedule(() => this.runFrame(), intervalMs);
  }

  private runFrame(): void {
    if (this.state !== 'running') return;
    const now = nowMs();
    const dt = now - this.lastWallMs;
    this.lastWallMs = now;
    this.accumulatorMs += dt;

    const effectiveIntervalMs = this.nominalIntervalMs / this.speed;
    let ticks = Math.floor(this.accumulatorMs / effectiveIntervalMs);
    if (ticks > this.maxCatchUpTicks) ticks = this.maxCatchUpTicks; // anti-spiral
    if (ticks > 0) {
      this.accumulatorMs -= ticks * effectiveIntervalMs;
      this.stepFn(ticks);
      this.onTick?.(ticks);
    }
    this.scheduleNext();
  }

  private cancelHandle(): void {
    if (this.handle !== null) {
      this.cancel(this.handle);
      this.handle = null;
    }
  }
}

function sanitizeSpeed(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n) || n <= 0) return 1;
  // Cap at 16× — strategy games rarely want faster, and very high values
  // lead to single-frame batches that defeat the smoothness benefit.
  return Math.min(n, 16);
}

function nowMs(): number {
  // performance.now is available in browser + Node 16+ + Bun.
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}
